import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { OverlayContainer } from '@angular/cdk/overlay';
import { AsyncPipe, NgIf, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
  LOCALE_ID,
  OnDestroy,
  PLATFORM_ID,
  afterNextRender,
  effect,
  inject,
  untracked
} from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarModule
} from '@angular/material/snack-bar';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet
} from '@angular/router';
import {
  IsOpenPipe,
  OwnerPipe,
  VakisService
} from '@vaki/shared/data-access/firestore';
import { LANGUAGES as LANGS, Vaki } from '@vaki/shared/util/interfaces';
import {
  ERROR_MATSNACKBAR_OPTIONS,
  MATSNACKBAR_OPTIONS,
  NotifyService,
  SharedUiNotificationsComponent
} from '@vaki/shared/util/notify';
import { Observable, Subject, merge } from 'rxjs';
import {
  filter,
  map,
  shareReplay,
  startWith,
  takeUntil,
  tap
} from 'rxjs/operators';

import {
  MatBottomSheet,
  MatBottomSheetModule,
  MatBottomSheetRef
} from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';

import { AuthService } from '@vaki/shared/auth/data-access';
import { CartService } from '@vaki/vakers/checkout/data-access';
import { VakersCheckoutUiCartPreviewComponent } from '@vaki/vakers/checkout/ui/cart-preview';
import { AppStateService } from '@vaki/vakers/shell/data-access';
import { VakersShellUiFooterComponent } from '@vaki/vakers/shell/ui/footer';
import { VakersShellUiMenuComponent } from '@vaki/vakers/shell/ui/menu';
import { VakersShellUiToolbarComponent } from '@vaki/vakers/shell/ui/toolbar';
import { LANGUAGES } from '@vaki/vakers/shell/util';
import { VakersVakisUiFreeContributionBoxComponent } from '@vaki/vakers/vakis/ui/free-contribution-box';
import { IntercomService } from '@vaki/shared/data-access/intercom';
@Component({
  selector: 'lib-main-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    VakersShellUiToolbarComponent,
    VakersShellUiFooterComponent,
    VakersShellUiMenuComponent,
    OwnerPipe,
    IsOpenPipe,
    NgIf,
    AsyncPipe,
    MatSnackBarModule,
    MatDialogModule,
    MatBottomSheetModule,
    MatSidenavModule
  ],
  templateUrl: './main-nav.component.html',
  styleUrl: './main-nav.component.scss'
})
export class VakersShellFeatureMainNavComponent implements OnDestroy {
  LANGUAGES = LANGUAGES;
  LANGS = LANGS;
  destroy$ = new Subject();
  // promoBanner$: Observable<Topbar> | undefined;
  @HostBinding('class') className = '';

  authService = inject(AuthService);

  private intercomService = inject(IntercomService);
  public readonly breakpointObserver = inject(BreakpointObserver);
  private readonly breakpointObserver$: Observable<boolean> =
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(
        takeUntil(this.destroy$),
        map((result) => result.matches),
        shareReplay()
      );
  tabs = false;
  hideSideNav: boolean | undefined;
  hideFooter$: Observable<boolean>;

  public readonly vakisService = inject(VakisService);

  private readonly matBottomSheet = inject(MatBottomSheet);
  private readonly matDialog: MatDialog = inject(MatDialog);
  readonly appStateService = inject(AppStateService);

  matBottomSheetRef:
    | MatBottomSheetRef<VakersCheckoutUiCartPreviewComponent, unknown>
    | undefined;

  cartService = inject(CartService);

  private readonly locale = inject(LOCALE_ID);

  private readonly router = inject(Router);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly snackbar: MatSnackBar,
    private readonly statusService: NotifyService,
    @Inject(PLATFORM_ID) private platformId: string,
    private readonly overlay: OverlayContainer
  ) {
    afterNextRender(() => {
      this.authService.fetchUserIp();
      this.intercomService.loadIntercom();
      if (this.locale) {
        const appLanguage =
          this.locale.split('-')[0] === 'en' ? LANGUAGES.EN : LANGUAGES.ES;
        this.appStateService.setAppLanguage(appLanguage);
      }
    });
    effect(() => {
      const userCountry = this.authService.userCountryCode();
      untracked(() =>
        this.appStateService.changeUserCurrency(
          this.appStateService
            .countries()
            .find(
              (country) =>
                country.isoCode.toLowerCase() === userCountry.toLowerCase()
            )?.currency || 'USD'
        )
      );
    });
    this.breakpointObserver$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => (this.hideSideNav = res));

    const data$: Observable<Record<string, string>> = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(new NavigationEnd(0, '/', '/')),
      tap(() => {
        if (isPlatformBrowser(this.platformId)) {
          window.scrollTo(0, 0);
        }
      }),
      map(() => {
        let child = this.activatedRoute.firstChild;
        let data = {};
        while (child) {
          data = { ...data, ...child.snapshot?.data };
          child = child.firstChild;
        }
        return data;
      })
    );

    this.hideFooter$ = data$.pipe(
      map((data) => Object.prototype.hasOwnProperty.call(data, 'hideFooter'))
    );

    merge(
      this.statusService.error$.pipe(
        map((notification) => ({
          data: { notification, isError: true },
          ...ERROR_MATSNACKBAR_OPTIONS
        }))
      ),
      this.statusService.success$.pipe(
        map((notification) => ({
          data: { notification, isError: false },
          ...MATSNACKBAR_OPTIONS
        }))
      )
    )
      .pipe(
        takeUntil(this.destroy$),
        tap((options: MatSnackBarConfig) =>
          this.snackbar.openFromComponent(
            SharedUiNotificationsComponent,
            options
          )
        )
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
  }

  onActive(container: HTMLDivElement): void {
    container.scrollTop = 0;
  }

  darkModeChange(darkMode: boolean): void {
    const darkClassName = 'dark-theme';
    this.className = darkMode ? darkClassName : '';
    if (darkMode) {
      this.overlay.getContainerElement().classList.add(darkClassName);
    } else {
      this.overlay.getContainerElement().classList.remove(darkClassName);
    }
  }

  onSearch(keyword: string): void {
    this.router.navigate(['/explorar'], { queryParams: { keyword } });
  }

  openCart(): void {
    this.matBottomSheetRef = this.matBottomSheet.open(
      VakersCheckoutUiCartPreviewComponent
    );
  }

  supportVaki(dialogData: {
    vaki: Vaki;
    currencies: string[];
    available: boolean;
    currentCurrency: string;
  }) {
    this.matDialog.open(VakersVakisUiFreeContributionBoxComponent, {
      data: {
        vaki: dialogData.vaki,
        currencies: dialogData.currencies,
        available: dialogData.available,
        currentCurrency: dialogData.currentCurrency
      }
    });
  }
}
